import { useState } from 'react';
import * as Endpoint from "../../../../endpoints";

import { useLoading } from '../../../../sg-hooks/useLoading';
import { getSgApiService } from '../../../../sg-core/services/ApiService2';
import { makeSuccess, TResult, makeFailure } from '../../../../sg-core/utils/Result';

import IFormType from '../../../interfaces/IFormType';
import IAudit, { INewAudit } from '../../../interfaces/IAudit';

import { useAuthContext } from '../../../auth/AuthenticationProvider';
import { getAuditService } from '../../../services/auditService';
import { useAuditCache } from '../useAuditsCache';
import { makeDefaultAuditTitle } from '../../../utils/kierrokset.helper';
import { useFormDefinition } from '../../formType/useFormDefinition';
import { useCategory } from '../../categories/useCategory';

interface IUseAuditInitializationData {
    auditId: IAudit["id"] | null;
    initialize: () => Promise<TResult<IAudit>>;
    initializing: boolean;
    initializeError?: string;
    initializedAt?: Date;
}

export type IUseAuditInitializationHook = (formType: IFormType, baseAudit: IAudit | null) => IUseAuditInitializationData;

/**
 * Hook that creates empty audit in draft state.
 */
export const useAuditInitialization: IUseAuditInitializationHook = (formType, baseAudit) => {

    const {
        loading: initializing,
        loadError: initializeError,
        startLoading,
        loadSuccess,
        loadErrored,
    } = useLoading();

    const { token, user, anonymousSession } = useAuthContext();

    const { data: formDefinition } = useFormDefinition(formType.formDefinition)
    const { data: category } = useCategory(formType.category)

    const [initializedAt, setInitializedAt] = useState<Date>();
    const [auditId, setAuditId] = useState<IAudit["id"] | null>(null);

    const service = getAuditService(getSgApiService(token), Endpoint.auditoinnit, anonymousSession);
    const { revalidateStaticLists, mutateInstance } = useAuditCache();

    const updateCache = async (audit: IAudit) => {
        await revalidateStaticLists();
        await mutateInstance(audit);
    };

    const initialize = async (): Promise<TResult<IAudit>> => {
        if (initializing) {
            return makeFailure(Error("initialization prevented"))
        }
        try {

            if (!formDefinition || !category || !formType) throw Error("Failed to load form meta");
            if (!user?.hprPersonId) throw Error("Failed to load user");

            const dateNow = new Date();
            dateNow.setHours(0);
            dateNow.setMinutes(0);
            dateNow.setMilliseconds(0);

            let auditDraft: INewAudit = {
                metaData: {
                    timestamp: dateNow,
                    responsiblePerson: user.hprPersonId,
                    participants: [],
                    images: [],
                    ...(baseAudit?.metaData || {})
                },

                // formData: baseAudit?.formData || {},
                formData: {},
                notes: {},

                title: makeDefaultAuditTitle(formDefinition, dateNow),
                target: baseAudit?.target || "",

                formType: formType.id,
                formSchema: formDefinition.schema,

                creator: user.hprPersonId,
                createdAt: new Date(),
            };

            startLoading();
            const result = await service.createAudit(auditDraft);

            if (result.isSuccess) {
                setAuditId(result.value.audit.id);
                setInitializedAt(new Date());
                loadSuccess();

                updateCache(result.value.audit)
                return makeSuccess(result.value.audit);
            };
            throw result.error;
        } catch (error) {
            console.log(error)
            loadErrored((error as Error).message);
            return makeFailure(error);
        };
    };

    return {
        auditId,
        initialize,
        initializing,
        initializeError,
        initializedAt
    };
};
